import React from 'react';
import moment from 'moment';
import { Badge } from '@/components/ui/badge';
import {
	PhoneOutgoing,
	PhoneIncoming,
	PhoneMissed,
	Voicemail,
	PhoneForwarded,
	FileText,
	Loader2,
	Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Call } from '@/types/validationSchemas/CallSchema';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/reducers';
import CallRecordingPlayer from './CallRecordingPlayer';
import { setTranscriptView } from '@/actions/userInterface';
import formatPhoneForUser from '@/utils/formatPhoneForUser';
import accessLevels from '@/utils/accessLevels';
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

interface CallCardProps {
	call: Call;
}

const CallCard: React.FC<CallCardProps> = ({ call }) => {
	const users = useSelector((state: RootState) => state.users);
	const { user, twilioNumber, products } = useSelector(
		(state: RootState) => state.auth,
	);

	const hasVoiceProduct = products && products.includes('voice');
	const dispatch = useDispatch();
	const [deleteRecordingOpen, setDeleteRecordingOpen] = React.useState(false);
	const [isDeletingRecording, setIsDeletingRecording] = React.useState(false);
	const [locallyDeleted, setLocallyDeleted] = React.useState(false);

	// Format duration in seconds to readable format: 154 -> "2m 34s"
	const formatDuration = (seconds: number | undefined): string => {
		if (!seconds || seconds === 0) return '0s';
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		if (mins > 0) {
			return `${mins}m ${secs}s`;
		}
		return `${secs}s`;
	};

	// Get sentiment styling based on sentiment value
	const getSentimentStyle = (sentiment: string) => {
		if (sentiment === 'positive') {
			return {
				bg: 'bg-green-100 dark:bg-green-900/30',
				text: 'text-green-700 dark:text-green-300',
				label: 'Positive',
			};
		}
		if (sentiment === 'negative') {
			return {
				bg: 'bg-red-100 dark:bg-red-900/30',
				text: 'text-red-700 dark:text-red-300',
				label: 'Negative',
			};
		}
		return {
			bg: 'bg-gray-100 dark:bg-gray-900/30',
			text: 'text-gray-700 dark:text-gray-300',
			label: 'Neutral',
		};
	};

	// Helper to detect voicemails (explicit status or legacy detection)
	const isVoicemail = (call: Call) => {
		// New voicemails have explicit status
		if (call.callStatus === 'voicemail') return true;

		// Legacy voicemails: inbound no-answer with recording and transcription
		return (
			call.direction === 'inbound' &&
			call.callStatus === 'no-answer' &&
			!!call.recording?.url &&
			!!call.transcription?.text
		);
	};

	// Helper to detect forwarded calls
	// A forwarded call has a different "to" than the business Twilio number
	// (the call was routed to an external phone number instead of an in-app agent)
	const isForwardedCall = (call: Call) => {
		return (
			call.direction === 'inbound' &&
			!call._user &&
			call.callStatus === 'completed' &&
			call.to !== twilioNumber
		);
	};

	const userOnCall = users.find((u) => u._id === call._user);
	const assignedUserName = userOnCall?.name || userOnCall?.username;
	const answeredByName =
		call._user?.toString() === user?._id?.toString()
			? 'You'
			: assignedUserName;

	// Get call title and icon based on direction and status
	const getCallInfo = () => {
		const status = call.callStatus?.toLowerCase();
		const isOutbound = call.direction === 'outbound';
		const callIsVoicemail = isVoicemail(call);
		const name = answeredByName;

		if (isOutbound) {
			switch (status) {
				case 'completed':
					return {
						title: `${name} called`,
						icon: PhoneOutgoing,
						iconBg: 'bg-blue-100 dark:bg-blue-900/50',
						iconColor: 'text-blue-600 dark:text-blue-400',
					};
				case 'no-answer':
					return {
						title: 'No answer',
						icon: PhoneMissed,
						iconBg: 'bg-red-100 dark:bg-red-900/50',
						iconColor: 'text-red-600 dark:text-red-400',
					};
				case 'busy':
					return {
						title: 'Line busy',
						icon: PhoneMissed,
						iconBg: 'bg-red-100 dark:bg-red-900/50',
						iconColor: 'text-red-600 dark:text-red-400',
					};
				case 'failed':
					return {
						title: 'Call failed',
						icon: PhoneMissed,
						iconBg: 'bg-red-100 dark:bg-red-900/50',
						iconColor: 'text-red-600 dark:text-red-400',
					};
				default:
					return {
						title: `${name} called`,
						icon: PhoneOutgoing,
						iconBg: 'bg-blue-100 dark:bg-blue-900/50',
						iconColor: 'text-blue-600 dark:text-blue-400',
					};
			}
		} else {
			switch (status) {
				case 'ringing':
					return {
						title: 'Incoming call',
						icon: PhoneIncoming,
						iconBg: 'bg-green-100 dark:bg-green-900/50',
						iconColor: 'text-green-600 dark:text-green-400',
					};
				case 'busy':
					// The dialed device(s) or carrier reported busy; the caller was
					// routed to voicemail, and a left message promotes the record to
					// 'voicemail'. Not "rejected by <name>": _user on a missed
					// inbound call is only a round-robin attempt.
					return {
						title: 'Line busy',
						icon: PhoneMissed,
						iconBg: 'bg-red-100 dark:bg-red-900/50',
						iconColor: 'text-red-600 dark:text-red-400',
					};
				case 'completed':
					if (isForwardedCall(call)) {
						return {
							title: `Call forwarded to ${formatPhoneForUser(
								call.to,
							)}`,
							icon: PhoneForwarded,
							iconBg: 'bg-purple-100 dark:bg-purple-900/50',
							iconColor: 'text-purple-600 dark:text-purple-400',
						};
					}
					// no one answered the call and it's not a forwarded call
					if (!call._user && !isForwardedCall(call)) {
						return {
							title: 'Missed call',
							icon: PhoneMissed,
							iconBg: 'bg-red-100 dark:bg-red-900/50',
							iconColor: 'text-red-600 dark:text-red-400',
						};
					}
					return {
						title: `${name} answered`,
						icon: PhoneOutgoing,
						iconBg: 'bg-blue-100 dark:bg-blue-900/50',
						iconColor: 'text-blue-600 dark:text-blue-400',
					};
				case 'voicemail':
					return {
						title: 'Voicemail',
						icon: Voicemail,
						iconBg: 'bg-red-100 dark:bg-red-900/50',
						iconColor: 'text-red-600 dark:text-red-400',
					};
				case 'no-answer':
					if (callIsVoicemail) {
						return {
							title: 'Voicemail',
							icon: Voicemail,
							iconBg: 'bg-red-100 dark:bg-red-900/50',
							iconColor: 'text-red-600 dark:text-red-400',
						};
					}
					return {
						title: 'Missed call',
						icon: PhoneMissed,
						iconBg: 'bg-red-100 dark:bg-red-900/50',
						iconColor: 'text-red-600 dark:text-red-400',
					};
				case 'failed':
					return {
						title: 'Call failed',
						icon: PhoneMissed,
						iconBg: 'bg-red-100 dark:bg-red-900/50',
						iconColor: 'text-red-600 dark:text-red-400',
					};
				default:
					return {
						title: 'Incoming call',
						icon: PhoneIncoming,
						iconBg: 'bg-green-100 dark:bg-green-900/50',
						iconColor: 'text-green-600 dark:text-green-400',
					};
			}
		}
	};

	const callInfo = getCallInfo();
	const CallIcon = callInfo.icon;

	const hasRecording =
		hasVoiceProduct &&
		call.recording?.url &&
		call.recording?.status === 'completed' &&
		(call.duration || call.recording?.duration) > 0;

	const hasTranscript =
		hasVoiceProduct &&
		call.transcription?.text &&
		call?.transcription?.sid &&
		call.transcription?.status !== 'pending';

	const hasSummary =
		hasVoiceProduct &&
		call?.summary?.bullets &&
		call.summary.bullets.length > 0 &&
		call.summary?.status === 'completed';

	// Only use fixed width when there's content that could expand (recording/transcript)
	const needsFixedWidth = hasRecording || hasTranscript || hasSummary;

	const callDuration = isVoicemail(call)
		? call.recording?.duration
		: call.duration;

	const isOutbound = call.direction === 'outbound';

	const isRecordingDeleted =
		locallyDeleted || call.recording?.status === 'deleted';

	const isPrivileged =
		user?.accessLevel === accessLevels.OWNER ||
		user?.accessLevel === accessLevels.LOCATION_ADMIN;

	const canDeleteRecording = Boolean(
		isPrivileged &&
		hasVoiceProduct &&
		call._id &&
		call.recording?.sid &&
		call.recording?.status === 'completed',
	);

	const handleDeleteRecordingConfirm = async () => {
		if (!call._id) return;
		try {
			setIsDeletingRecording(true);
			const res = await axios.delete(
				`/api/voice/calls/${call._id}/recording`,
				{ withCredentials: true },
			);

			if (res.data?.success) {
				toast.success('Recording deleted');
				setLocallyDeleted(true);
				setDeleteRecordingOpen(false);
			} else {
				toast.error('Failed to delete recording');
			}
		} catch (err) {
			const msg = axios.isAxiosError(err)
				? err.response?.data?.error || 'Failed to delete recording'
				: 'Failed to delete recording';
			toast.error(msg);
		} finally {
			setIsDeletingRecording(false);
		}
	};

	const TranscriptButton = () => {
		if (call.transcription?.status === 'failed') {
			return (
				<div className="flex items-center gap-2 text-muted-foreground">
					<FileText className="w-3.5 h-3.5 shrink-0" />
					<span className="text-xs">No transcript</span>
				</div>
			);
		}

		// Note: CallSchema uses 'in_progress' (not 'processing') for transcription.
		if (call.transcription?.status === 'in_progress') {
			return (
				<Badge
					variant="secondary"
					className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800"
				>
					Processing
					<Loader2 className="size-3 animate-spin ml-1" />
				</Badge>
			);
		}

		return (
			<button
				className="group flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold max-md:min-h-11 touch:min-h-11 max-md:text-[13px] text-gray-600 hover:bg-white hover:text-blue-600 hover:shadow-sm transition-all border border-transparent hover:border-gray-100 dark:text-gray-400 dark:hover:bg-slate-800 dark:hover:text-blue-400 dark:hover:border-slate-700"
				onClick={() =>
					dispatch(
						setTranscriptView({
							transcription: call.transcription?.text,
							transcriptionStatus: call.transcription?.status as
								| 'pending'
								| 'processing'
								| 'completed'
								| 'failed'
								| undefined,
							isVoicemail: isVoicemail(call),
							agentName: answeredByName,
						}),
					)
				}
			>
				<FileText className="w-3.5 h-3.5" />
				Transcript
			</button>
		);
	};

	return (
		<div
			className={cn(
				'flex w-full py-2',
				isOutbound ? 'justify-end' : 'justify-start',
			)}
		>
			<article
				className={cn(
					'bg-white dark:bg-card rounded-2xl border border-gray-100 dark:border-border shadow-sm overflow-hidden flex flex-col',
					needsFixedWidth
						? 'w-full max-w-[400px]'
						: 'w-auto inline-block', // Allow shrinking if no rich content
					isOutbound ? 'mr-3' : 'ml-3', // Add margins similar to ChatBubble
				)}
			>
				{/* HEADER */}
				<div
					className={cn(
						'p-2 px-4 pt-4 flex items-start justify-between gap-4',
						!hasSummary && !hasRecording && !hasTranscript
							? 'pb-4'
							: 'pb-2',
					)}
				>
					<div className="flex gap-3">
						<div
							className={cn(
								'p-2 rounded-full flex items-center justify-center h-10 w-10 flex-shrink-0',
								callInfo.iconBg,
								callInfo.iconColor,
							)}
						>
							<CallIcon className="w-5 h-5" />
						</div>
						<div>
							<h3 className="font-semibold text-gray-900 dark:text-foreground text-sm md:text-base whitespace-nowrap">
								{callInfo.title}
							</h3>

							<p className="text-xs text-gray-500 dark:text-muted-foreground font-medium mt-0.5">
								{moment(call.createdAt).format('LT')}
								{callDuration > 0 && <span> • </span>}
								<span>
									{callDuration > 0 &&
										formatDuration(callDuration)}
								</span>
							</p>
						</div>
					</div>
					{hasVoiceProduct &&
						call.summary?.sentiment &&
						call.summary.sentiment !== 'unknown' && (
							<Badge
								variant="secondary"
								className={cn(
									'px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide uppercase flex-shrink-0',
									getSentimentStyle(call.summary.sentiment)
										.bg,
									getSentimentStyle(call.summary.sentiment)
										.text,
								)}
							>
								{
									getSentimentStyle(call.summary.sentiment)
										.label
								}
							</Badge>
						)}
				</div>

				{/* CONTENT AREA */}
				<div className="px-2 flex-grow">
					{hasSummary ? (
						<div className="mt-2 mb-4 bg-gray-50 dark:bg-muted/50 rounded-xl p-4 border border-gray-100/50 dark:border-border/50">
							<div className="flex items-center gap-2 mb-2">
								<div className="bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-300 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide">
									AI Summary
								</div>
							</div>
							<ul className="space-y-2">
								{call.summary.bullets.map((point, i) => (
									<li
										key={i}
										className="text-sm text-gray-600 dark:text-muted-foreground flex gap-2 items-start leading-relaxed"
									>
										<span className="block w-1.5 h-1.5 mt-1.5 rounded-full bg-purple-300 dark:bg-purple-700 flex-shrink-0" />
										{point}
									</li>
								))}
							</ul>
						</div>
					) : (
						// Collapse if no summary, unless we need spacing for other elements,
						// but for simple cards without summary/recording, minimal spacing is better.
						// If recording exists but no summary, we might want a small spacer.
						(hasRecording || hasTranscript) && (
							<div className="h-2"></div>
						)
					)}
				</div>

				{/* FOOTER: Player & Transcript */}
				{(hasRecording || hasTranscript || isRecordingDeleted) && (
					<div className="p-2 pt-0">
						{hasRecording && !isRecordingDeleted ? (
							<CallRecordingPlayer
								recordingUrl={call.recording.url}
								callDuration={callDuration}
								variant={
									isVoicemail(call) ? 'voicemail' : 'default'
								}
								footerAction={
									<div className="flex items-center justify-end gap-2">
										{hasTranscript ? (
											<TranscriptButton />
										) : null}
										{canDeleteRecording && (
											<AlertDialog
												open={deleteRecordingOpen}
												onOpenChange={
													setDeleteRecordingOpen
												}
											>
												<button
													type="button"
													className="group flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold max-md:min-h-11 touch:min-h-11 max-md:text-[13px] text-gray-600 hover:bg-white hover:text-red-600 hover:shadow-sm transition-all border border-transparent hover:border-gray-100 dark:text-gray-400 dark:hover:bg-slate-800 dark:hover:text-red-400 dark:hover:border-slate-700"
													onClick={() =>
														setDeleteRecordingOpen(
															true,
														)
													}
												>
													<Trash2 className="w-4 h-4 mr-2" />
													Delete
												</button>
												<AlertDialogContent>
													<AlertDialogHeader>
														<AlertDialogTitle>
															Delete call
															recording?
														</AlertDialogTitle>
														<AlertDialogDescription className="text-left">
															This permanently
															deletes the
															recording and
															removes the
															transcript and AI
															summary from this
															call. This cannot be
															undone.
														</AlertDialogDescription>
													</AlertDialogHeader>
													<AlertDialogFooter>
														<AlertDialogCancel
															disabled={
																isDeletingRecording
															}
														>
															Cancel
														</AlertDialogCancel>
														<Button
															type="button"
															disabled={
																isDeletingRecording
															}
															className="bg-red-600 text-white hover:bg-red-600/90 focus-visible:ring-red-600"
															onClick={() =>
																void handleDeleteRecordingConfirm()
															}
														>
															{isDeletingRecording
																? 'Deleting…'
																: 'Delete'}
														</Button>
													</AlertDialogFooter>
												</AlertDialogContent>
											</AlertDialog>
										)}
									</div>
								}
							/>
						) : isRecordingDeleted ? (
							<div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800">
								<div className="text-xs font-semibold text-muted-foreground">
									Recording deleted
								</div>
							</div>
						) : (
							/* Only transcript available, no recording */
							<div className="p-2 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-100 dark:border-slate-800 flex justify-end">
								<TranscriptButton />
							</div>
						)}
					</div>
				)}
			</article>
		</div>
	);
};

export default CallCard;
